import { Head, Link } from '@inertiajs/react'
import { MainLayout } from '@/components/layouts'
import { ArrowLeft, Star, Trophy, Award, MessageSquare } from 'lucide-react'

interface Profile {
  id: number
  pseudo: string
  specialty: string | null
  level: number
  community_name: string | null
  member_since: string
}

interface Badge {
  id: number
  name: string
  emoji: string
  description: string | null
  earned_at: string | null
}

interface Stats {
  total_points: number
  level: number
  rank: number
  badges_count: number
  posts_count: number
}

interface ShowProps {
  profile: Profile
  badges: Badge[]
  stats: Stats
}

function getLevelName(level: number): string {
  const levels: Record<number, string> = {
    1: 'Novice',
    2: 'Apprenti',
    3: 'Confirmé',
    4: 'Expert',
    5: 'Maître'
  }
  return levels[level] || `Niveau ${level}`
}

export default function Show({ profile, badges, stats }: ShowProps) {
  return (
    <MainLayout title={`Profil de ${profile.pseudo}`}>
      <Head title={`Profil de ${profile.pseudo}`} />

      <div>
        {/* Back button */}
        <Link
          href="/community"
          className="inline-flex items-center gap-2 text-text-gray hover:text-tessan-green transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à la communauté
        </Link>

        {/* Profile header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-start gap-6">
            {/* Avatar */}
            <div className="w-24 h-24 rounded-full bg-tessan-green flex items-center justify-center text-white text-3xl font-bold shrink-0">
              {profile.pseudo.charAt(0).toUpperCase()}
            </div>

            {/* Info */}
            <div className="flex-1">
              <h1 className="text-2xl font-heading font-bold text-text-dark mb-1">
                {profile.pseudo}
              </h1>
              {profile.specialty && (
                <p className="text-text-gray mb-2">{profile.specialty}</p>
              )}
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-tessan-yellow/20 text-tessan-green rounded-full font-medium">
                  <Star className="w-4 h-4" />
                  {getLevelName(profile.level)}
                </span>
                {profile.community_name && (
                  <span className="text-text-gray">
                    {profile.community_name}
                  </span>
                )}
                <span className="text-text-gray">
                  Membre depuis {profile.member_since}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={<Star className="w-5 h-5" />}
            value={stats.total_points}
            label="Points"
            color="bg-yellow-100 text-yellow-600"
          />
          <StatCard
            icon={<Trophy className="w-5 h-5" />}
            value={`#${stats.rank}`}
            label="Classement"
            color="bg-purple-100 text-purple-600"
          />
          <StatCard
            icon={<Award className="w-5 h-5" />}
            value={stats.badges_count}
            label="Badges"
            color="bg-green-100 text-green-600"
          />
          <StatCard
            icon={<MessageSquare className="w-5 h-5" />}
            value={stats.posts_count}
            label="Publications"
            color="bg-blue-100 text-blue-600"
          />
        </div>

        {/* Badges section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-heading font-semibold text-text-dark mb-4">
            Badges obtenus ({badges.length})
          </h2>

          {badges.length === 0 ? (
            <p className="text-text-gray text-center py-8">
              Aucun badge obtenu pour le moment
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {badges.map((badge) => (
                <div
                  key={badge.id}
                  className="flex flex-col items-center p-4 bg-gray-50 rounded-xl text-center"
                >
                  <span className="text-4xl mb-2">{badge.emoji}</span>
                  <p className="font-medium text-text-dark text-sm mb-1">
                    {badge.name}
                  </p>
                  {badge.description && (
                    <p className="text-xs text-text-gray line-clamp-2">
                      {badge.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  )
}

function StatCard({
  icon,
  value,
  label,
  color
}: {
  icon: React.ReactNode
  value: number | string
  label: string
  color: string
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center mb-3`}>
        {icon}
      </div>
      <p className="text-2xl font-bold text-text-dark">{value}</p>
      <p className="text-sm text-text-gray">{label}</p>
    </div>
  )
}
